import type React from 'react';

import { type Brand } from '@/consts/brands';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { type SmartySuggestion } from '@/types/productSelectionAndCheckout/types';

const DEBOUNCE_WAIT_MS = 200;
let timeoutId: ReturnType<typeof setTimeout> | undefined;
let prevSearchString = '';

export const getSuggestions = (
  brand: Brand,
  searchString: string,
  setSuggestions: React.Dispatch<React.SetStateAction<SmartySuggestion[]>>,
) => {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }

  if (
    searchString.trim() !== '' &&
    searchString.toLowerCase() !== prevSearchString.toLowerCase()
  ) {
    prevSearchString = searchString;

    timeoutId = setTimeout(async () => {
      timeoutId = undefined;

      try {
        const { data } =
          await ProductSelectionAndCheckoutService.getSmartyAutosuggestResults(
            brand,
            searchString,
          );

        setSuggestions(data.suggestions || []);
      } catch (e) {
        setSuggestions([]);
      }
    }, DEBOUNCE_WAIT_MS);
  } else {
    setSuggestions([]);
  }
};

// Source: https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
const escapeRegex = (regexStr: string) => {
  return regexStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const getSearchMatchesInAddressString = (
  searchString: string,
  addressString: string,
) => {
  const regex = new RegExp(escapeRegex(searchString), 'gi');
  const matches = [];
  let result;

  while ((result = regex.exec(addressString))) {
    matches.push({
      start: result.index,
      end: result.index + searchString.length,
    });
  }

  return matches;
};

export const splitAddressStringIntoParts = (
  searchString: string,
  addressString: string,
) => {
  if (searchString.trim() === '') {
    return [
      {
        substring: searchString,
      },
    ];
  }

  const matches = getSearchMatchesInAddressString(searchString, addressString);

  if (matches.length === 0) {
    return [
      {
        substring: addressString,
      },
    ];
  }

  const addressStringParts: {
    style?: string;
    substring: string;
  }[] = [];

  matches.forEach((match, index) => {
    if (index === 0) {
      addressStringParts.push({
        substring: addressString.substring(0, match.start),
      });
    }

    addressStringParts.push({
      style: 'bold',
      substring: addressString.substring(match.start, match.end),
    });

    if (matches[index + 1]) {
      addressStringParts.push({
        substring: addressString.substring(match.end, matches[index + 1].start),
      });
    } else {
      addressStringParts.push({
        substring: addressString.substring(match.end),
      });
    }
  });

  return addressStringParts.filter(
    (addressStringPart) => addressStringPart.substring !== '',
  );
};
